import { isAdminPlatformPwaPath } from '../../app/pwa/adminPwaSurfaces.js';
import { appLogger } from '../../shared/logging/appLogger.js';
import { getTenantFromPath, requireTenantFromPath } from '../../shared/tenant.js';

const ADMIN_TOKEN_DEV_KEY = 'admin_token_dev';

export interface TokenStorage {
  get(): string | null;
  /** @deprecated Prefer setMemory for dual|cookie — LS write is mode=off migration only. */
  set(token: string): void;
  /** In-memory session JWT for dual-mode Bearer; not persisted. */
  setMemory(token: string | null): void;
  clearMemory(): void;
  remove(): void;
}

/**
 * Dual-mode bridge:
 * - `setMemory` / `clearMemory` — phase-2 session (no LS JWT write on login)
 * - `get` still reads LS for mode=off migration / stale tabs
 * - `set` kept for explicit legacy callers; AuthProvider login must not call it
 *
 * G24 Residual (gateway-inventory): `get()` is **mode=off legacy only** — still reads
 * `/^admin_token_/` LS for dual Bearer / migration. Boot / cold-start paths must NOT
 * use `get()` as an auth gate (AuthProvider owns cookie-first `/me`; AdminPwaColdStart
 * awaits that boot via `platformMeBootBridge` — G3 at most one identity probe).
 * Pass path = setMemory only. Exit greps for `tokenStorage.set` / `admin_token_` →
 * anti-pattern-greps.
 */
class TokenStorageImpl implements TokenStorage {
  private memoryToken: string | null = null;

  private key(): string {
    if (typeof window !== 'undefined' && isAdminPlatformPwaPath(window.location.pathname)) {
      return ADMIN_TOKEN_DEV_KEY;
    }
    const tenant = requireTenantFromPath();
    return `admin_token_${tenant}`;
  }

  get(): string | null {
    if (this.memoryToken !== null && this.memoryToken.length > 0) {
      return this.memoryToken;
    }
    try {
      if (typeof window !== 'undefined' && isAdminPlatformPwaPath(window.location.pathname)) {
        return localStorage.getItem(ADMIN_TOKEN_DEV_KEY);
      }
      const tenant = getTenantFromPath();
      if (tenant) {
        const tenantToken = localStorage.getItem(`admin_token_${tenant}`);
        if (tenantToken) {
          return tenantToken;
        }
      }
      // SUPER_DEV/DEV may SPA-navigate onto /{tenant} while a legacy JWT still lives
      // under admin_token_dev — fall back for mode=off migration reads.
      return localStorage.getItem(ADMIN_TOKEN_DEV_KEY);
    } catch {
      return null;
    }
  }

  setMemory(token: string | null): void {
    this.memoryToken = token !== null && token.length > 0 ? token : null;
  }

  clearMemory(): void {
    this.memoryToken = null;
  }

  set(token: string): void {
    try {
      localStorage.setItem(this.key(), token);
    } catch (error) {
      appLogger.error('Failed to save token to localStorage:', error);
    }
  }

  remove(): void {
    this.memoryToken = null;
    try {
      localStorage.removeItem(this.key());
    } catch (error) {
      appLogger.error('Failed to remove token from localStorage:', error);
    }
  }
}

export const tokenStorage = new TokenStorageImpl();
